import { topojson } from 'chartjs-chart-geo'
import worldTopology from 'world-atlas/countries-110m.json'
import countries from 'i18n-iso-countries'
import enLocale from 'i18n-iso-countries/langs/en.json'

countries.registerLocale(enLocale)

let cachedFeatures = null
let cachedAlpha2FeatureMap = null

/**
 * Get every country as a GeoJSON feature from the bundled world-atlas topology
 * (110m resolution - small enough for a report page, not survey-grade).
 * Memoized since the topology never changes at runtime.
 *
 * @returns {Array<Object>} GeoJSON features, each keyed by ISO 3166-1 numeric id
 */
export function getWorldFeatures() {
    if (cachedFeatures === null) {
        cachedFeatures = topojson.feature(
            worldTopology,
            worldTopology.objects.countries
        ).features
    }

    return cachedFeatures
}

/**
 * Build a lookup from ISO 3166-1 alpha-2 country code (the format returned by
 * the geographic exposure report) to its world-atlas GeoJSON feature, so report
 * data can be joined onto the map. A Map (rather than a plain object keyed by
 * dynamic country code) avoids an object-injection footgun and lets lookups use
 * `.get()`/`.has()` instead of bracket access. Memoized alongside getWorldFeatures
 * since the topology never changes at runtime.
 *
 * @returns {Map<string, Object>} Map of alpha-2 code to GeoJSON feature
 */
export function alpha2ToFeatureMap() {
    if (cachedAlpha2FeatureMap === null) {
        cachedAlpha2FeatureMap = new Map()

        getWorldFeatures().forEach((feature) => {
            const alpha2 = countries.numericToAlpha2(feature.id)
            if (alpha2) {
                cachedAlpha2FeatureMap.set(alpha2, feature)
            }
        })
    }

    return cachedAlpha2FeatureMap
}

/**
 * Get the English display name for an ISO 3166-1 alpha-2 country code, falling
 * back to the code itself if it isn't recognized.
 *
 * @param {string} alpha2Code - ISO 3166-1 alpha-2 country code
 * @returns {string} Display name
 */
export function countryName(alpha2Code) {
    return countries.getName(alpha2Code, 'en') ?? alpha2Code
}
